import jsPDF from 'jspdf';

export interface ChartData {
  type: 'pie' | 'bar' | 'line' | 'area' | 'metric';
  title: string;
  description: string;
  data: any[];
  dataKeys?: string[];
  xAxisKey?: string;
  colors?: string[];
  [key: string]: any;
}

export interface PDFDownloadOptions {
  data: any;
  filename?: string;
  title?: string;
  format?: 'pdf';
  dateRange?: string;
  tab?: string;
  selectedReports?: string[];
  charts?: ChartData[];
}

/**
 * Downloads a PDF report with the provided data
 * @param options - The options for generating the PDF
 */
export const downloadPDF = (options: PDFDownloadOptions): void => {
  const {
    data,
    filename = 'report.pdf',
    title = 'Report',
    format = 'pdf',
    dateRange = '',
    tab = '',
    selectedReports = [],
    charts = []
  } = options;

  if (format !== 'pdf') {
    console.warn('Only PDF format is currently supported');
    return;
  }

  const doc = new jsPDF();
  let yPosition = 30;

  // Add title
  doc.setFontSize(20);
  doc.text(title, 20, yPosition);
  yPosition += 30;

  // Add date range if provided
  if (dateRange) {
    doc.setFontSize(12);
    doc.text(`Date Range: ${dateRange}`, 20, yPosition);
    yPosition += 15;
  }

  // Add tab info if provided
  if (tab) {
    doc.setFontSize(12);
    doc.text(`Tab: ${tab}`, 20, yPosition);
    yPosition += 15;
  }

  // Add selected reports if provided
  if (selectedReports.length > 0) {
    doc.setFontSize(12);
    doc.text(`Selected Reports: ${selectedReports.join(', ')}`, 20, yPosition);
    yPosition += 20;
  }

  // Process charts if provided
  if (charts && charts.length > 0) {
    charts.forEach((chart, index) => {
      // Check if we need a new page
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 30;
      }

      // Add chart title
      doc.setFontSize(14);
      doc.text(chart.title, 20, yPosition);
      yPosition += 10;

      // Add chart description
      if (chart.description) {
        doc.setFontSize(10);
        const descLines = doc.splitTextToSize(chart.description, 170);
        descLines.forEach((line: string) => {
          doc.text(line, 20, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Generate chart image based on type
      let chartImage = '';
      switch (chart.type) {
        case 'pie':
          chartImage = createPieChartImage(chart.data, chart.title);
          break;
        case 'bar':
          chartImage = createBarChartImage(
            chart.data, 
            chart.title, 
            chart.dataKeys || [], 
            chart.xAxisKey || 'name',
            chart.colors
          );
          break;
        case 'line':
          chartImage = createLineChartImage(
            chart.data, 
            chart.title, 
            chart.dataKeys || [], 
            chart.xAxisKey || 'name',
            chart.colors
          );
          break;
        case 'area':
          chartImage = createAreaChartImage(
            chart.data, 
            chart.title, 
            chart.dataKeys || [], 
            chart.xAxisKey || 'name',
            chart.colors
          );
          break;
        default:
          console.warn(`Chart type ${chart.type} not supported`);
          return;
      }

      if (chartImage) {
        const imgWidth = 170;  // Increased size for better quality
        const imgHeight = 120;
        
        // Check if image fits on current page with more margin
        if (yPosition + imgHeight > 250) {
          doc.addPage();
          yPosition = 30;
        }
        
        // Add chart image with better quality settings
        try {
          doc.addImage(chartImage, 'PNG', 20, yPosition, imgWidth, imgHeight, '', 'FAST');
        } catch (error) {
          console.warn('Failed to add chart image:', error);
          // Fallback: add a text note instead
          doc.setFontSize(10);
          doc.text('Chart could not be rendered', 20, yPosition);
        }
        yPosition += imgHeight + 25;
      }

      // Add data summary for the chart
      if (chart.data && chart.data.length > 0) {
        doc.setFontSize(10);
        doc.text('Data Summary:', 20, yPosition);
        yPosition += 10;

        chart.data.slice(0, 5).forEach((item: any) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 30;
          }
          const itemText = `${item.name || item[chart.xAxisKey || 'name'] || 'Item'}: ${Object.keys(item)
            .filter(key => typeof item[key] === 'number')
            .map(key => `${key}: ${item[key]}`)
            .join(', ')}`;
          doc.text(itemText, 25, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
    });
  } else if (data) {
    // Fallback to original data handling
    if (typeof data === 'string' && data.startsWith('data:image')) {
      const imgWidth = 150;
      const imgHeight = 120;
      
      if (yPosition + imgHeight > 250) {
        doc.addPage();
        yPosition = 30;
      }
      
      doc.addImage(data, 'PNG', 20, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 20;
    } else {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Data Summary:', 20, yPosition);
      yPosition += 25;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      if (typeof data === 'object' && data !== null) {
        // Better object formatting
        const formatObject = (obj: any, indent = 0): string[] => {
          const lines: string[] = [];
          const spaces = '  '.repeat(indent);
          
          if (Array.isArray(obj)) {
            obj.forEach((item, index) => {
              lines.push(`${spaces}[${index}]:`);
              if (typeof item === 'object') {
                lines.push(...formatObject(item, indent + 1));
              } else {
                lines.push(`${spaces}  ${item}`);
              }
            });
          } else {
            Object.entries(obj).forEach(([key, value]) => {
              if (typeof value === 'object' && value !== null) {
                lines.push(`${spaces}${key}:`);
                lines.push(...formatObject(value, indent + 1));
              } else {
                lines.push(`${spaces}${key}: ${value}`);
              }
            });
          }
          return lines;
        };
        
        const formattedLines = formatObject(data);
        formattedLines.forEach((line: string) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 30;
          }
          doc.text(line, 20, yPosition);
          yPosition += 6;
        });
      } else {
        const dataString = String(data);
        const lines = doc.splitTextToSize(dataString, 170);
        lines.forEach((line: string) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 30;
          }
          doc.text(line, 20, yPosition);
          yPosition += 6;
        });
      }
    }
  }

  // Save the PDF
  doc.save(filename);
};

// Chart rendering functions
export const createPieChartImage = (data: any[], title: string, width = 600, height = 500): string => {
  const canvas = document.createElement('canvas');
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  // Set high DPI canvas
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  // Scale context for high DPI
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Clear canvas with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 5;

  let total = 0;
  data.forEach((item: any) => total += item.value || 0);

  if (total > 0) {
    let startAngle = 0;
    data.forEach((item: any, index: number) => {
      const angle = ((item.value || 0) / total) * 2 * Math.PI;
      const endAngle = startAngle + angle;

      // Draw pie slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = item.color || `hsl(${index * 40}, 70%, 50%)`;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw labels with better positioning
      const labelAngle = startAngle + angle / 2;
      const labelRadius = radius + 40;
      const labelX = centerX + labelRadius * Math.cos(labelAngle);
      const labelY = centerY + labelRadius * Math.sin(labelAngle);
      
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const percentage = ((item.value / total) * 100).toFixed(1);
      const text = `${item.name}\n${percentage}%`;
      const lines = text.split('\n');
      
      lines.forEach((line, index) => {
        ctx.fillText(line, labelX, labelY + (index - 0.5) * 16);
      });

      startAngle = endAngle;
    });
  }

  // Add title with better positioning
  ctx.fillStyle = '#1F2937';
  ctx.font = 'bold 20px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(title, centerX, 20);

  return canvas.toDataURL('image/png');
};

export const createBarChartImage = (data: any[], title: string, dataKeys: string[], xAxisKey: string, colors: string[] = [], width = 700, height = 500): string => {
  const canvas = document.createElement('canvas');
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  // Set high DPI canvas
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  // Scale context for high DPI
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Clear canvas with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  const margin = { top: 80, right: 60, bottom: 100, left: 80 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Find max value for scaling
  let maxValue = 0;
  data.forEach(item => {
    dataKeys.forEach(key => {
      if (typeof item[key] === 'number' && item[key] > maxValue) {
        maxValue = item[key];
      }
    });
  });

  const barSpacing = 10;
  const totalBars = data.length * dataKeys.length;
  const totalSpacing = (data.length - 1) * barSpacing;
  const availableWidth = chartWidth - totalSpacing;
  const barWidth = Math.max(availableWidth / totalBars, 20);
  const barGroupWidth = barWidth * dataKeys.length;

  // Draw bars with better spacing
  data.forEach((item, i) => {
    dataKeys.forEach((key, j) => {
      const value = item[key] || 0;
      const barHeight = Math.max((value / maxValue) * chartHeight, 0);
      const x = margin.left + i * (barGroupWidth + barSpacing) + j * barWidth;
      const y = margin.top + chartHeight - barHeight;

      // Draw bar with rounded corners
      ctx.fillStyle = colors[j] || `hsl(${j * 60}, 70%, 50%)`;
      ctx.fillRect(x, y, barWidth - 4, barHeight);
      
      // Add value labels on bars with better positioning
      if (value > 0) {
        ctx.fillStyle = '#374151';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(value.toString(), x + barWidth/2, y - 8);
      }
    });

    // Add x-axis labels with proper spacing
    ctx.fillStyle = '#374151';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const labelX = margin.left + i * (barGroupWidth + barSpacing) + barGroupWidth/2;
    const labelText = item[xAxisKey].toString();
    
    // Wrap long labels
    if (labelText.length > 10) {
      const words = labelText.split(' ');
      const maxWidth = barGroupWidth + 20;
      let line = '';
      let y = height - margin.bottom + 15;
      
      words.forEach((word: string, index: number) => {
        const testLine = line + word + ' ';
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth && index > 0) {
          ctx.fillText(line.trim(), labelX, y);
          line = word + ' ';
          y += 18;
        } else {
          line = testLine;
        }
      });
      ctx.fillText(line.trim(), labelX, y);
    } else {
      ctx.fillText(labelText, labelX, height - margin.bottom + 15);
    }
  });

  // Draw axes
  ctx.strokeStyle = '#E5E7EB';
  ctx.lineWidth = 1;
  
  // Y-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top);
  ctx.lineTo(margin.left, margin.top + chartHeight);
  ctx.stroke();
  
  // X-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top + chartHeight);
  ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
  ctx.stroke();

  // Add title
  ctx.fillStyle = '#000000';
  ctx.font = 'bold 16px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(title, width / 2, 30);

  return canvas.toDataURL('image/png');
};

export const createLineChartImage = (data: any[], title: string, dataKeys: string[], xAxisKey: string, colors: string[] = [], width = 700, height = 500): string => {
  const canvas = document.createElement('canvas');
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  // Set high DPI canvas
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  // Scale context for high DPI
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Clear canvas with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  const margin = { top: 80, right: 60, bottom: 100, left: 80 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Find max value for scaling
  let maxValue = 0;
  data.forEach(item => {
    dataKeys.forEach(key => {
      if (typeof item[key] === 'number' && item[key] > maxValue) {
        maxValue = item[key];
      }
    });
  });

  // Draw grid lines first
  ctx.strokeStyle = '#F3F4F6';
  ctx.lineWidth = 1;
  
  // Vertical grid lines
  data.forEach((_, i) => {
    const x = margin.left + (i / (data.length - 1)) * chartWidth;
    ctx.beginPath();
    ctx.moveTo(x, margin.top);
    ctx.lineTo(x, margin.top + chartHeight);
    ctx.stroke();
  });
  
  // Horizontal grid lines
  const ySteps = 5;
  for (let i = 0; i <= ySteps; i++) {
    const y = margin.top + (i / ySteps) * chartHeight;
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(margin.left + chartWidth, y);
    ctx.stroke();
  }

  // Draw lines with better styling
  dataKeys.forEach((key, keyIndex) => {
    const color = colors[keyIndex] || `hsl(${keyIndex * 60}, 70%, 50%)`;
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();

    let validPoints: {x: number, y: number, value: number}[] = [];
    
    data.forEach((item, i) => {
      const x = margin.left + (i / (data.length - 1)) * chartWidth;
      const value = item[key] || 0;
      const y = margin.top + chartHeight - (value / maxValue) * chartHeight;
      validPoints.push({x, y, value});
    });

    // Draw smooth line
    if (validPoints.length > 0) {
      ctx.moveTo(validPoints[0].x, validPoints[0].y);
      
      for (let i = 1; i < validPoints.length; i++) {
        ctx.lineTo(validPoints[i].x, validPoints[i].y);
      }
      ctx.stroke();

      // Draw points with values
      validPoints.forEach((point, i) => {
        // Draw point
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
        ctx.fill();
        
        // Add white border
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Add value label above point
        ctx.fillStyle = '#374151';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(point.value.toString(), point.x, point.y - 10);
      });
    }
  });

  // Draw main axes
  ctx.strokeStyle = '#D1D5DB';
  ctx.lineWidth = 2;
  
  // Y-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top);
  ctx.lineTo(margin.left, margin.top + chartHeight);
  ctx.stroke();
  
  // X-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top + chartHeight);
  ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
  ctx.stroke();

  // Add Y-axis labels
  ctx.fillStyle = '#6B7280';
  ctx.font = '12px Arial';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  
  for (let i = 0; i <= ySteps; i++) {
    const value = (maxValue / ySteps) * (ySteps - i);
    const y = margin.top + (i / ySteps) * chartHeight;
    ctx.fillText(Math.round(value).toString(), margin.left - 10, y);
  }

  // Add x-axis labels with rotation for long text
  ctx.fillStyle = '#374151';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  
  data.forEach((item, i) => {
    const x = margin.left + (i / (data.length - 1)) * chartWidth;
    const label = item[xAxisKey].toString();
    
    if (label.length > 8) {
      // Rotate long labels
      ctx.save();
      ctx.translate(x, height - margin.bottom + 15);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.fillText(label, 0, 0);
      ctx.restore();
    } else {
      ctx.fillText(label, x, height - margin.bottom + 15);
    }
  });

  // Add title with better positioning
  ctx.fillStyle = '#1F2937';
  ctx.font = 'bold 22px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(title, width / 2, 20);

  // Add legend if multiple data keys
  if (dataKeys.length > 1) {
    const legendY = height - 30;
    const legendItemWidth = 120;
    const startX = (width - (dataKeys.length * legendItemWidth)) / 2;
    
    dataKeys.forEach((key, index) => {
      const x = startX + index * legendItemWidth;
      const color = colors[index] || `hsl(${index * 60}, 70%, 50%)`;
      
      // Draw color box
      ctx.fillStyle = color;
      ctx.fillRect(x, legendY - 5, 15, 15);
      
      // Draw label
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(key, x + 20, legendY + 2.5);
    });
  }

  return canvas.toDataURL('image/png');
};

export const createAreaChartImage = (data: any[], title: string, dataKeys: string[], xAxisKey: string, colors: string[] = [], width = 700, height = 500): string => {
  const canvas = document.createElement('canvas');
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  // Set high DPI canvas
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  // Scale context for high DPI
  ctx.scale(devicePixelRatio, devicePixelRatio);

  // Clear canvas with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  const margin = { top: 80, right: 60, bottom: 100, left: 80 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  if (data.length === 0) {
    ctx.fillStyle = '#6B7280';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('No data available', width / 2, height / 2);
    return canvas.toDataURL('image/png');
  }

  // Calculate max value
  const allValues = data.flatMap(item => dataKeys.map(key => item[key] || 0));
  const maxValue = Math.max(...allValues, 1);

  // Draw grid lines
  ctx.strokeStyle = '#F3F4F6';
  ctx.lineWidth = 1;
  
  const ySteps = 5;
  for (let i = 0; i <= ySteps; i++) {
    const y = margin.top + (i / ySteps) * chartHeight;
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(margin.left + chartWidth, y);
    ctx.stroke();
  }

  // Draw areas (from bottom to top)
  dataKeys.forEach((key, keyIndex) => {
    const color = colors[keyIndex] || `hsl(${keyIndex * 60}, 70%, 50%)`;
    
    // Create gradient
    const gradient = ctx.createLinearGradient(0, margin.top, 0, margin.top + chartHeight);
    gradient.addColorStop(0, color + '80'); // 50% opacity at top
    gradient.addColorStop(1, color + '20'); // 12.5% opacity at bottom
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    
    // Start from bottom-left
    ctx.moveTo(margin.left, margin.top + chartHeight);
    
    // Draw area line
    data.forEach((item, i) => {
      const x = margin.left + (i / (data.length - 1)) * chartWidth;
      const value = item[key] || 0;
      const y = margin.top + chartHeight - (value / maxValue) * chartHeight;
      ctx.lineTo(x, y);
    });
    
    // Close path to bottom-right
    ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
    ctx.closePath();
    ctx.fill();
    
    // Draw line on top
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    data.forEach((item, i) => {
      const x = margin.left + (i / (data.length - 1)) * chartWidth;
      const value = item[key] || 0;
      const y = margin.top + chartHeight - (value / maxValue) * chartHeight;
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
    
    // Draw points
    data.forEach((item, i) => {
      const x = margin.left + (i / (data.length - 1)) * chartWidth;
      const value = item[key] || 0;
      const y = margin.top + chartHeight - (value / maxValue) * chartHeight;
      
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      ctx.fill();
      
      // White border
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  });

  // Draw main axes
  ctx.strokeStyle = '#D1D5DB';
  ctx.lineWidth = 2;
  
  // Y-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top);
  ctx.lineTo(margin.left, margin.top + chartHeight);
  ctx.stroke();
  
  // X-axis
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top + chartHeight);
  ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
  ctx.stroke();

  // Add Y-axis labels
  ctx.fillStyle = '#6B7280';
  ctx.font = '12px Arial';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  
  for (let i = 0; i <= ySteps; i++) {
    const value = (maxValue / ySteps) * (ySteps - i);
    const y = margin.top + (i / ySteps) * chartHeight;
    ctx.fillText(Math.round(value).toString(), margin.left - 10, y);
  }

  // Add x-axis labels
  ctx.fillStyle = '#374151';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  
  data.forEach((item, i) => {
    const x = margin.left + (i / (data.length - 1)) * chartWidth;
    const label = item[xAxisKey].toString();
    ctx.fillText(label, x, height - margin.bottom + 15);
  });

  // Add title
  ctx.fillStyle = '#1F2937';
  ctx.font = 'bold 22px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(title, width / 2, 20);

  return canvas.toDataURL('image/png');
};

// Chart data validation helper
const validateChartData = (chartData: ChartData): boolean => {
  if (!chartData || !chartData.data || !Array.isArray(chartData.data)) {
    console.warn('Invalid chart data: data must be an array');
    return false;
  }
  
  if (chartData.data.length === 0) {
    console.warn('Invalid chart data: data array is empty');
    return false;
  }
  
  if (!chartData.title || typeof chartData.title !== 'string') {
    console.warn('Invalid chart data: title must be a non-empty string');
    return false;
  }
  
  // Type-specific validation
  switch (chartData.type) {
    case 'pie':
      // Pie charts need data with name and value properties
      if (!chartData.data.every(item => item.name && (item.value !== undefined && item.value !== null))) {
        console.warn('Invalid pie chart data: each item must have name and value properties');
        return false;
      }
      break;
      
    case 'bar':
    case 'line':
    case 'area':
      // These charts need dataKeys and xAxisKey
      if (!chartData.dataKeys || !Array.isArray(chartData.dataKeys) || chartData.dataKeys.length === 0) {
        console.warn('Invalid chart data: dataKeys must be a non-empty array');
        return false;
      }
      
      if (!chartData.xAxisKey || typeof chartData.xAxisKey !== 'string') {
        console.warn('Invalid chart data: xAxisKey must be a non-empty string');
        return false;
      }
      
      // Check if all data items have the required keys
      const hasRequiredKeys = chartData.data.every(item => {
        return chartData.xAxisKey && item[chartData.xAxisKey] !== undefined &&
               chartData.dataKeys && chartData.dataKeys.some(key => item[key] !== undefined);
      });
      
      if (!hasRequiredKeys) {
        console.warn('Invalid chart data: not all data items have required keys');
        return false;
      }
      break;
      
    default:
      console.warn('Invalid chart type:', chartData.type);
      return false;
  }
  
  return true;
};

// Main chart rendering function
export const renderChart = (chartData: ChartData): string => {
  // Validate chart data first
  if (!validateChartData(chartData)) {
    // Return empty chart with error message
    const canvas = document.createElement('canvas');
    const width = chartData.width || 400;
    const height = chartData.height || 300;
    
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#F3F4F6';
      ctx.fillRect(0, 0, width, height);
      
      ctx.fillStyle = '#EF4444';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Invalid Chart Data', width / 2, height / 2 - 10);
      
      ctx.fillStyle = '#6B7280';
      ctx.font = '12px Arial';
      ctx.fillText('Please check console for details', width / 2, height / 2 + 15);
    }
    
    return canvas.toDataURL('image/png');
  }
  
  const { type, data, title, dataKeys = [], xAxisKey = '', colors = [], width, height } = chartData;
  
  try {
    switch (type) {
      case 'pie':
        return createPieChartImage(data, title, width, height);
      case 'bar':
        return createBarChartImage(data, title, dataKeys, xAxisKey, colors, width, height);
      case 'line':
        return createLineChartImage(data, title, dataKeys, xAxisKey, colors, width, height);
      case 'area':
        return createAreaChartImage(data, title, dataKeys, xAxisKey, colors, width, height);
      default:
        console.warn('Unknown chart type:', type);
        return '';
    }
  } catch (error) {
    console.error('Error rendering chart:', error);
    
    // Return error chart
    const canvas = document.createElement('canvas');
    const w = width || 400;
    const h = height || 300;
    
    canvas.width = w;
    canvas.height = h;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#FEF2F2';
      ctx.fillRect(0, 0, w, h);
      
      ctx.fillStyle = '#DC2626';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Chart Rendering Error', w / 2, h / 2);
    }
    
    return canvas.toDataURL('image/png');
  }
};

export const getPieChartDataPdf = (data: any): string => {
  return createPieChartImage(data, 'Pie Chart Report');
};